// src/pages/AirbnbPortalPage.js
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Alert, Modal, Form, Badge } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAirbnbAuth } from '../context/AirbnbAuthContext.js';
import { useAirbnbBookings } from '../hooks/useAirbnbBookings.js';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

export default function AirbnbPortalPage() {
  const navigate = useNavigate();
  const { client, logout, isAuthenticated, loading: authLoading } = useAirbnbAuth();
  const {
    bookings,
    loading: bookingsLoading,
    error: bookingsError,
    fetchBookings,
    createBooking,
    updateBooking,
    cancelBooking
  } = useAirbnbBookings();

  const [selectedDate, setSelectedDate] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [newDate, setNewDate] = useState(null);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/airbnb/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Load bookings on mount
  useEffect(() => {
    if (isAuthenticated) {
      fetchBookings();
    }
  }, [isAuthenticated, fetchBookings]);

  const handleDateClick = (date) => {
    // Only allow future dates
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (date < today) {
      setError('Cannot book past dates');
      setTimeout(() => setError(''), 3000);
      return;
    }

    setSelectedDate(date);
    setShowConfirmModal(true);
  };

  const handleConfirmBooking = async () => {
    setShowConfirmModal(false);
    setError('');
    setSuccess('');

    // Format date as YYYY-MM-DD in local timezone (not UTC)
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    // Pass client info for calendar sync
    const clientInfo = {
      name: client.name,
      email: client.email,
      phone: client.phone,
      address: client.address
    };

    const result = await createBooking(dateStr, clientInfo);

    if (result.success) {
      setSuccess('Booking created successfully! You will receive a confirmation email.');
      setSelectedDate(null);
      setTimeout(() => setSuccess(''), 5000);
    } else {
      setError(result.error || 'Error creating booking');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleEditClick = (booking) => {
    setEditingBooking(booking);
    setNewDate(new Date(booking.bookingDate));
    setShowEditModal(true);
  };

  const handleConfirmEdit = async () => {
    if (!newDate || !editingBooking) return;

    setShowEditModal(false);
    setError('');
    setSuccess('');

    // Format date as YYYY-MM-DD in local timezone (not UTC)
    const year = newDate.getFullYear();
    const month = String(newDate.getMonth() + 1).padStart(2, '0');
    const day = String(newDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    console.log('📅 Updating booking to date:', dateStr, 'from:', newDate);

    const result = await updateBooking(editingBooking.id, dateStr);

    if (result.success) {
      setSuccess('Booking updated successfully!');
      setEditingBooking(null);
      setNewDate(null);
      setTimeout(() => setSuccess(''), 5000);
    } else {
      setError(result.error || 'Error updating booking');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) {
      return;
    }

    setError('');
    setSuccess('');

    const result = await cancelBooking(bookingId);

    if (result.success) {
      setSuccess('Booking cancelled successfully!');
      setTimeout(() => setSuccess(''), 5000);
    } else {
      setError(result.error || 'Error cancelling booking');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/airbnb/login');
  };

  if (authLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!client) return null;

  // Get booked dates for highlighting
  const bookedDates = bookings
    .filter(b => b.status === 'confirmed')
    .map(b => new Date(b.bookingDate).toDateString());

  const tileClassName = ({ date }) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (date < today) {
      return 'past-date';
    }

    if (bookedDates.includes(date.toDateString())) {
      return 'booked-date';
    }

    return null;
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '30px 0',
        marginBottom: '30px',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
      }}>
        <Container>
          <Row className="align-items-center">
            <Col>
              <h2 style={{ color: 'white', margin: 0, fontWeight: '700' }}>
                Booking Portal
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.9)', margin: '5px 0 0 0' }}>
                Welcome, {client.email}
              </p>
            </Col>
            <Col xs="auto">
              <Button
                variant="light"
                onClick={handleLogout}
                style={{ fontWeight: '600' }}
              >
                Sign Out
              </Button>
            </Col>
          </Row>
        </Container>
      </div>

      <Container>
        {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}
        {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
        {bookingsError && <Alert variant="danger">{bookingsError}</Alert>}

        <Row>
          {/* Calendário */}
          <Col lg={7} className="mb-4">
            <Card style={{ borderRadius: '15px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
              <Card.Body style={{ padding: '30px' }}>
                <h4 style={{ marginBottom: '20px', fontWeight: '600' }}>
                  📅 Select a Date
                </h4>

                <p style={{ color: '#666', marginBottom: '20px' }}>
                  Click on a date to make a booking.
                </p>

                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <Calendar
                    onChange={handleDateClick}
                    value={selectedDate}
                    minDate={new Date()}
                    tileClassName={tileClassName}
                    locale="en-NZ"
                  />
                </div>

                <div style={{ marginTop: '20px' }}>
                  <small style={{ display: 'block', marginBottom: '8px' }}>
                    <span style={{
                      display: 'inline-block',
                      width: '12px',
                      height: '12px',
                      background: '#4CAF50',
                      borderRadius: '50%',
                      marginRight: '8px'
                    }}></span>
                    Date with booking
                  </small>
                </div>

                <style>
                  {`
                    .react-calendar {
                      width: 100% !important;
                      max-width: 500px;
                      border: none !important;
                      font-family: inherit;
                    }

                    .react-calendar__tile--active {
                      background: #667eea !important;
                      color: white !important;
                    }

                    .react-calendar__tile--now {
                      background: #e3f2fd !important;
                    }

                    .past-date {
                      color: #ccc !important;
                      background: #f5f5f5 !important;
                    }

                    .booked-date {
                      background: #4CAF50 !important;
                      color: white !important;
                      font-weight: bold;
                    }

                    .react-calendar__tile:enabled:hover {
                      background: #f0f0f0;
                    }
                  `}
                </style>
              </Card.Body>
            </Card>
          </Col>

          {/* Lista de Bookings */}
          <Col lg={5} className="mb-4">
            <Card style={{ borderRadius: '15px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
              <Card.Body style={{ padding: '30px' }}>
                <h4 style={{ marginBottom: '20px', fontWeight: '600' }}>
                  📋 My Bookings
                </h4>

                {bookingsLoading && (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                )}

                {!bookingsLoading && bookings.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999' }}>
                    <p style={{ fontSize: '48px', margin: '0 0 10px 0' }}>📅</p>
                    <p>You don't have any bookings yet</p>
                  </div>
                )}

                {!bookingsLoading && bookings.length > 0 && (
                  <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    {bookings
                      .filter(b => b.status === 'confirmed')
                      .sort((a, b) => new Date(a.bookingDate) - new Date(b.bookingDate))
                      .map((booking) => (
                        <div
                          key={booking.id}
                          style={{
                            background: '#f8f9fa',
                            padding: '15px',
                            borderRadius: '10px',
                            marginBottom: '15px',
                            border: '1px solid #e0e0e0'
                          }}
                        >
                          <div style={{ marginBottom: '10px' }}>
                            <strong style={{ fontSize: '16px', color: '#333' }}>
                              {new Date(booking.bookingDate).toLocaleDateString('en-NZ', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </strong>
                          </div>

                          <div style={{ display: 'flex', gap: '10px' }}>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => handleEditClick(booking)}
                            >
                              ✏️ Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleCancelBooking(booking.id)}
                            >
                              ❌ Cancel
                            </Button>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Confirm Booking Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Booking</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedDate && (
            <>
              <p style={{ marginBottom: '10px' }}>
                <strong>Date:</strong> {selectedDate.toLocaleDateString('en-NZ', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
              <p style={{ color: '#666', fontSize: '14px' }}>
                A confirmation email will be sent after creating the booking.
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmBooking}
            disabled={bookingsLoading}
          >
            {bookingsLoading ? 'Creating...' : 'Confirm Booking'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Booking Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Booking</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingBooking && (
            <>
              <p style={{ marginBottom: '20px', color: '#666' }}>
                Select the new date for the booking:
              </p>

              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <Calendar
                  onChange={setNewDate}
                  value={newDate}
                  minDate={new Date()}
                  locale="en-NZ"
                />
              </div>

              {newDate && (
                <div style={{ marginTop: '20px', padding: '15px', background: '#f8f9fa', borderRadius: '8px' }}>
                  <p style={{ margin: 0 }}>
                    <strong>New date:</strong>{' '}
                    {newDate.toLocaleDateString('en-NZ', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirmEdit}
            disabled={bookingsLoading || !newDate}
          >
            {bookingsLoading ? 'Updating...' : 'Save Changes'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
